import React, { useEffect, useState } from 'react';
import { Amplify } from 'aws-amplify';
import awsmobile from '../aws-exports';
import { get, post, put, del, ApiError } from 'aws-amplify/api';
// Chakra used to be imported here, but the app has no ChakraProvider, so every
// colorScheme/size/spacing prop resolved against an empty theme and did nothing —
// this page was really being styled by !important overrides in its CSS.
// Replaced with native elements + tokens.
import './IncidentsCRUD.css';

Amplify.configure(awsmobile);

// Severity colour by weight, so the scale reads the same here as in the rankings.
// Moved from the deleted Severity Scale page.
function toneFor(points) {
    if (points >= 10) return { color: 'var(--sev-critical)',     bg: 'var(--sev-critical-bg)' };
    if (points >= 7)  return { color: 'var(--sev-very-serious)', bg: 'var(--sev-very-serious-bg)' };
    if (points >= 5)  return { color: 'var(--sev-severe)',       bg: 'var(--sev-severe-bg)' };
    if (points >= 3)  return { color: 'var(--sev-moderate)',     bg: 'var(--sev-moderate-bg)' };
    return              { color: 'var(--sev-normal)',       bg: 'var(--sev-normal-bg)' };
}

const IncidentsCRUD = () => {
    const [incidents, setIncidents] = useState([]);
    // Fetched, not hardcoded. This list used to be five literals with names but no
    // points, duplicating caps-categories-dev — so the page could not show a weight, and
    // renaming or reweighting a category in the table left this file silently wrong.
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [formData, setFormData] = useState({ name: '', category_id: '' });
    const [isEditing, setIsEditing] = useState(false);
    const [currentIncidentId, setCurrentIncidentId] = useState(null);

    useEffect(() => {
        async function fetchAll() {
            try {
                // Both in flight together: the types are the page and the scale is the key
                // they are grouped by, so there is no useful state with one and not the other.
                const [typesOp, categoriesOp] = [
                    get({ apiName: 'incidents', path: '/incidents/categories' }),
                    get({ apiName: 'categories', path: '/categories' }),
                ];
                const [typesRes, categoriesRes] = await Promise.all([
                    typesOp.response,
                    categoriesOp.response,
                ]);
                const [types, scale] = await Promise.all([
                    typesRes.body.json(),
                    categoriesRes.body.json(),
                ]);
                setIncidents(types);
                setCategories(scale);
                setLoading(false);
            } catch (e) {
                handleError(e);
            }
        }

        fetchAll();
    }, []);

    const handleError = (e) => {
        console.log('API call failed:', e);
        if (e instanceof ApiError && e.response) {
            const errorResponse = JSON.parse(e.response.body);
            setError(`Error: ${errorResponse.message}`);
        } else {
            setError('Failed to perform operation');
        }
        setLoading(false);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    const handleCategoryChange = (e) => {
        const selectedOption = e.target.options[e.target.selectedIndex].text;
        
        setFormData({ 
            ...formData, 
            category_id: e.target.value,
            category_name: selectedOption // If you want to save the name as well
        });
    };

    const handleCreateIncident = async () => {
        try {
            console.log('Creating incident:', formData);
            
            const restOperation = post({
                apiName: 'incidents',
                path: '/incidents/categories',
                options: {
                    body: formData
                }
            });
            const { body } = await restOperation.response;
            console.log('Incident created:', body);
            const newIncident = await body.json();
            setIncidents([...incidents, newIncident.item]);
            resetForm();
        } catch (e) {
            handleError(e);
        }
    };
    

    const handleEditIncident = (incident) => {
        setFormData({ name: incident.name, category_id: incident.category_id });
        setIsEditing(true);
        setCurrentIncidentId(incident.id);
    };

    const handleUpdateIncident = async () => {
        try {
            const restOperation = put({
                apiName: 'incidents', // Replace with your API name
                path: `/incidents/categories/${currentIncidentId}`,
                options: {
                    body: formData
                }
            });
            const { body } = await restOperation.response;
            const updatedIncident = await body.json();
    
            // Remove the old incident and add the updated one
            setIncidents(prevIncidents => {
                const filteredIncidents = prevIncidents.filter(incident => incident.id !== currentIncidentId);
                return [...filteredIncidents, updatedIncident.item];
            });
    
            resetForm();
        } catch (e) {
            handleError(e);
        }
    };
    

    const handleDeleteIncident = async (id) => {
        try {
            await del({
                apiName: 'incidents', // Replace with your API name
                path: `/incidents/categories/${id}`
            });
            setIncidents(incidents.filter(incident => incident.id !== id));
        } catch (e) {
            handleError(e);
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (isEditing) {
            handleUpdateIncident();
        } else {
            handleCreateIncident();
        }
    };

    const resetForm = () => {
        setFormData({ name: '', category_id: '' });
        setIsEditing(false);
        setCurrentIncidentId(null);
    };

    if (loading) return <p className="loading-state" role="status">Loading scale…</p>;
    if (error) return <p className="error-state" role="alert">{error}</p>;

    // Ordered by weight, and driven by the scale rather than by the types — so every
    // severity appears even when nothing is filed under it. A tier with no types is
    // information (the AI can still assign it, and nothing describes when to), which the
    // old grouping hid by only rendering categories that happened to have members.
    // String-compared because the API returns ids as strings and the rows store them
    // either way; parseInt used to be enough only because the ids were hardcoded numbers.
    const scale = [...categories].sort((a, b) => Number(b.points) - Number(a.points));
    const topPoints = scale.reduce((max, c) => Math.max(max, Number(c.points) || 0), 0);

    const groups = scale.map((category) => ({
        ...category,
        points: Number(category.points) || 0,
        items: incidents.filter(
            (incident) => String(incident.category_id) === String(category.id)
        ),
    }));

    // Anything pointing at a category id the scale does not contain. Silently dropping
    // these would make types disappear from a page whose whole job is to list them.
    const orphans = incidents.filter(
        (incident) => !scale.some((c) => String(c.id) === String(incident.category_id))
    );

    return (
        <div className="incidents-container">
            <div className="page-header">
                {/* This page manages incident TYPES, not incidents that occurred —
                    the old title ("AI Machine Learning - Incidents Management") said
                    neither. The Severity Scale page was folded in here: it rendered the
                    same five categories this page groups by, so as its own route it was a
                    legend printed on a separate sheet from the thing it explained. */}
                <h2 className="page-title">Incident Types</h2>
                <p className="page-subtitle">
                    Wehaa scores incidents against a shared severity scale to keep work standards
                    and accountability consistent. Each severity below carries the points shown,
                    and a person's Liability Ranking score is the sum of their tickets' points.
                    Expand a severity to see the incident types the AI can assign at that weight.
                </p>
            </div>

            <form onSubmit={handleSubmit} className="incident-form card">
                <div className="field">
                    <label className="field-label" htmlFor="incident-name">Incident name</label>
                    <input
                        id="incident-name"
                        className="field-input"
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="e.g. Production outage"
                        required
                    />
                </div>
                <div className="field">
                    <label className="field-label" htmlFor="incident-category">Severity category</label>
                    <select
                        id="incident-category"
                        className="field-input"
                        value={formData.category_id}
                        onChange={handleCategoryChange}
                        required
                    >
                        <option value="">Select category</option>
                        {categories.map(category => (
                            <option key={category.id} value={category.id}>
                                {category.name}
                            </option>
                        ))}
                    </select>
                </div>
                <div className="form-actions">
                    <button type="submit" className="btn btn-primary">
                        {isEditing ? 'Update type' : 'Create type'}
                    </button>
                    {isEditing && (
                        <button type="button" onClick={resetForm} className="btn btn-neutral">
                            Cancel
                        </button>
                    )}
                </div>
            </form>

            {!scale.length ? (
                <p className="empty-state">No severity categories are configured.</p>
            ) : (
                <div className="accordion">
                    {groups.map((group) => {
                        const tone = toneFor(group.points);
                        return (
                            // <details> gives open/close and keyboard support natively —
                            // no library, no ARIA to hand-maintain.
                            <details key={group.id} className="accordion-item">
                                <summary className="accordion-summary">
                                    {/* The weight badge and bar are what the Severity Scale
                                        page used to be. Carrying them on the summary means
                                        the scale is legible with every group collapsed. */}
                                    <span
                                        className="severity-points"
                                        style={{ color: tone.color, background: tone.bg }}
                                    >
                                        {group.points}
                                    </span>
                                    <span className="severity-name">{group.name}</span>
                                    <span className="severity-bar-track" aria-hidden="true">
                                        <span
                                            className="severity-bar-fill"
                                            style={{
                                                width: topPoints ? `${(group.points / topPoints) * 100}%` : '0%',
                                                background: tone.color,
                                            }}
                                        />
                                    </span>
                                    <span className="accordion-count">{group.items.length}</span>
                                </summary>
                                {!group.items.length ? (
                                    <p className="accordion-empty">
                                        No incident types defined at this severity.
                                    </p>
                                ) : (
                                    <ul className="accordion-panel">
                                        {group.items.map((incident) => (
                                            <li key={incident.id} className="incident-item">
                                                <span className="incident-name">{incident.name}</span>
                                                <span className="incident-actions">
                                                    <button
                                                        type="button"
                                                        className="btn btn-neutral"
                                                        onClick={() => handleEditIncident(incident)}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-danger"
                                                        onClick={() => handleDeleteIncident(incident.id)}
                                                    >
                                                        Delete
                                                    </button>
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </details>
                        );
                    })}

                    {orphans.length > 0 && (
                        <details className="accordion-item">
                            <summary className="accordion-summary">
                                <span className="severity-name">Uncategorized</span>
                                <span className="accordion-count">{orphans.length}</span>
                            </summary>
                            <ul className="accordion-panel">
                                {orphans.map((incident) => (
                                    <li key={incident.id} className="incident-item">
                                        <span className="incident-name">{incident.name}</span>
                                        <span className="incident-actions">
                                            <button
                                                type="button"
                                                className="btn btn-neutral"
                                                onClick={() => handleEditIncident(incident)}
                                            >
                                                Edit
                                            </button>
                                            <button
                                                type="button"
                                                className="btn btn-danger"
                                                onClick={() => handleDeleteIncident(incident.id)}
                                            >
                                                Delete
                                            </button>
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </details>
                    )}
                </div>
            )}
        </div>
    );
};

export default IncidentsCRUD;
